import {
  IUIUrval,
  TEDInputUrvalType,
  TEDUrvalConditionType,
  IUISavedUrvalProps,
  IUIPage,
  UIUrvalConditionObjectType,
  IBooleanConditionObject,
  IChecklistConditionObject,
  ITypeTreeConditionObject,
  INumericConditionObject,
  IStringConditionObject,
  IExtendedConditionObject,
  TEDUrvalType,
  IFilterConditionObject,
} from '../../../Types/UrvalTypes';
import {
  IClientAssociationModel,
  IClientBusinessModel,
} from '../../../Types/ClientModelTypes';

import { unnest, replace, toUpper, isEmpty, compose } from 'ramda';
import {
  TEDSelectionConditionType,
  TEDSelectionOperator,
} from '../../../globalQueryTypes';
import { URVAL_CHECKLIST_NO_VALUE_VALUE } from '../Consts/UrvalConsts';

/**
 * Converts an IUISavedSelectionProps to an TEDInputSelection
 */
export const convertSavedUrvalPropertiesToInputUrval = (
  savedSelectionProps: IUISavedUrvalProps
): TEDInputUrvalType => {
  const {
    namn,
    beskrivning,
    rutinID,
    kontoplanID,
    usagecode,
    collection,
    synlighet,
    synligAnvID,
    synligGruppID,
  } = savedSelectionProps;

  return {
    namn,
    beskrivning: beskrivning ? beskrivning : '',
    rutinID,
    kontoplanID,
    usagecode,
    collection,
    synlighet,
    synligAnvID,
    synligGruppID,
  };
};

/**
 * Conditions
 */
const getAssociationName = (assoc: IClientAssociationModel) =>
  assoc.AssociationName;

const convertBoolean = (
  booleanConditionObject: IBooleanConditionObject
): TEDUrvalConditionType => {
  const { attribute, modelPath, condition } = booleanConditionObject;

  return {
    namn: attribute.AttributeName,
    ass: modelPath != null ? modelPath.map(getAssociationName) : null,
    typ: TEDSelectionConditionType.Boolean,
    booleanCondition: { no: condition.no, yes: condition.yes },
  };
};

const convertFilter = (
  filterConditionObject: IFilterConditionObject
): TEDUrvalConditionType => {
  const { attribute, modelPath, condition } = filterConditionObject;

  return {
    namn: attribute.AttributeName,
    ass: modelPath != null ? modelPath.map(getAssociationName) : null,
    typ: TEDSelectionConditionType.Filter,
    filterCondition: { active: condition },
  };
};

export const convertStringToEnumString: (
  str: string
) => // Typningen av compose tillåter bara 6 parametrar, så castar compose till any här
string = (compose as any)(
  replace(/Å/g, 'A'),
  replace(/Ä/g, 'A'),
  replace(/Ö/g, 'O'),
  replace(/ /g, '_'),
  replace(/-/g, '_'),
  replace(/\//g, '_'),
  toUpper
);

const convertChecklistCondition = (
  checklistConditionObject: IChecklistConditionObject
): Array<string | null> => {
  return checklistConditionObject.condition.map(value => {
    if (value === URVAL_CHECKLIST_NO_VALUE_VALUE) {
      return null;
    } else {
      return value;
    }
  });
};

const convertChecklist = (
  checklistConditionObject: IChecklistConditionObject
): TEDUrvalConditionType => {
  const { attribute, modelPath } = checklistConditionObject;

  return {
    namn: attribute.AttributeName,
    ass: modelPath != null ? modelPath.map(getAssociationName) : null,
    typ: TEDSelectionConditionType.Enum,
    enumCondition: {
      selected: convertChecklistCondition(checklistConditionObject),
    },
  };
};

const convertTypeTree = (
  typeTreeConditionObject: ITypeTreeConditionObject
): TEDUrvalConditionType => {
  const { attribute, modelPath, condition } = typeTreeConditionObject;

  return {
    namn: attribute.AttributeName,
    ass: modelPath != null ? modelPath.map(getAssociationName) : null,
    typ: TEDSelectionConditionType.Enum,
    enumCondition: { selected: condition },
  };
};

const convertNumeric = (
  numericConditionObject: INumericConditionObject
): TEDUrvalConditionType[] => {
  const {
    attribute,
    modelPath,
    condition: conditions,
  } = numericConditionObject;

  return conditions.map(condition => ({
    namn: attribute.AttributeName,
    ass: modelPath != null ? modelPath.map(getAssociationName) : null,
    typ: TEDSelectionConditionType.Value,
    valueCondition: {
      op: condition.op,
      value: condition.values.map(val => ({
        value: val.value != null ? val.value.toString() : null,
        tovalue: val.toValue != null ? val.toValue.toString() : null,
      })),
    },
  }));
};

const convertString = (
  stringConditionObject: IStringConditionObject
): TEDUrvalConditionType[] => {
  const { attribute, modelPath, condition: conditions } = stringConditionObject;

  return conditions.map(condition => ({
    namn: attribute.AttributeName,
    ass: modelPath != null ? modelPath.map(getAssociationName) : null,
    typ: TEDSelectionConditionType.Value,
    valueCondition: {
      op: condition.op,
      value: condition.values.map(val => ({
        value: val.value != null ? val.value.toString() : null,
        tovalue: val.toValue != null ? val.toValue.toString() : null,
      })),
    },
  }));
};

const convertExtended = (
  extendedConditionObject: IExtendedConditionObject
): TEDUrvalConditionType[] => {
  const {
    association,
    modelPath,
    condition: conditions,
  } = extendedConditionObject;

  return conditions.map(condition => ({
    namn: association.AssociationName,
    ass: modelPath != null ? modelPath.map(getAssociationName) : null,
    typ: TEDSelectionConditionType.Extended,
    extendedCondition: {
      extop: condition.op,
      extconditions: condition.in.map(page => convertPage(page)),
    },
  }));
};

const convertCondition = (
  condition: UIUrvalConditionObjectType
): TEDUrvalConditionType | TEDUrvalConditionType[] => {
  switch (condition.type) {
    case 'Boolean': {
      return convertBoolean(condition);
    }
    case 'Filter': {
      return convertFilter(condition);
    }
    case 'Checklist': {
      return convertChecklist(condition);
    }
    case 'TypeTree': {
      return convertTypeTree(condition);
    }
    case 'Numeric': {
      return convertNumeric(condition);
    }
    case 'String': {
      return convertString(condition);
    }
    case 'Extended': {
      return convertExtended(condition);
    }
  }
};

const convertPage = (page: IUIPage): TEDUrvalConditionType[] => {
  return unnest<ReadonlyArray<TEDUrvalConditionType>>(
    page.conditionObjects.map(condition =>
      convertCondition(condition)
    ) as ReadonlyArray<TEDUrvalConditionType>
  );
};

export const convertUIUrvalToTEDUrval = (
  uiUrval: IUIUrval,
  baseCollection?: IClientBusinessModel,
  ownId?: string
): TEDUrvalType => {
  let TEDUrval = uiUrval.pages.map(page => convertPage(page));
  if (baseCollection) {
    TEDUrval[0].push({
      namn: 'id',
      ass: [baseCollection.RootType.TypeValue],
      typ: TEDSelectionConditionType.Value,
      valueCondition: {
        op: TEDSelectionOperator.equals,
        value: [
          {
            value: ownId,
          },
        ],
      },
    });
  }

  if (isEmpty(TEDUrval)) {
    TEDUrval = [[]];
  }
  return TEDUrval;
};

export const tryConvertUIUrvalToTEDUrval = (
  uiUrval: IUIUrval | undefined | null
): TEDUrvalType | undefined => {
  if (uiUrval == null) {
    return undefined;
  }

  return convertUIUrvalToTEDUrval(uiUrval);
};
